/* eslint-disable consistent-return */
/* eslint-disable no-param-reassign */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useLazyQuery, useApolloClient } from '@apollo/client';
import { useDispatch } from 'react-redux';
import SwipeableViews from 'react-swipeable-views';
import { virtualize } from 'react-swipeable-views-utils';
import classNames from 'classnames';
import PropTypes from 'prop-types';

// app modules: actions
import { featureUsed } from 'actions/featureUsage';

// app modules: components
import ContentView from 'components/ContentView';

// app: services
import history from 'services/history';
import route from 'services/route';

import useTextSelection from 'hooks/useTextSelection';
import useBodyClassWatcher from 'hooks/useBodyClassWatcher';

import { EDITOR_FOCUSED_CLASS_PREFIX } from 'constants/index';

// queries
import SwipeableContentPreviewQuery from './SwipeableContentPreviewQuery.gql';

const VirtualizeSwipeableViews = virtualize(SwipeableViews);

const BOOK_INDEX_SIZE = 30; // Must be an even number
const MIDDLE_PAGE = BOOK_INDEX_SIZE / 2;
const DEFAULT_CURSOR = { index: MIDDLE_PAGE, animate: true };

let swipeFeatureUsageNotified = false;
let keyboardFeatureUsageNotified = false;
const canUseIdleCallback = typeof requestIdleCallback === 'function';

function SwipeableContentView(props) {
  const { id, className, slideClassName, disableAnimation, ...otherProps } = props;
  const apolloClient = useApolloClient();
  const bookId = id.substring(0, 9);
  const containerRef = useRef();
  const settledTimeout = useRef(null);
  const pendingNavigation = useRef(null);
  const lastViewChangeTargetIndex = useRef(0);
  const loadingMore = useRef(false);
  const dispatch = useDispatch();
  const [mainItemIndex, setMainItemIndex] = useState(MIDDLE_PAGE);
  const [cursor, setCursor] = useState(DEFAULT_CURSOR);
  const [items, setItems] = useState(
    new Array(BOOK_INDEX_SIZE).map((item, index) => (index === MIDDLE_PAGE ? { id } : null)),
  );
  const readyTimerRef = useRef();
  const [ready, setReady] = useState(false);
  const [isScrollingInTable, setIsScrollingInTable] = useState(false);

  const selectedContent = useTextSelection();
  const isWritingSomething = useBodyClassWatcher(EDITOR_FOCUSED_CLASS_PREFIX);

  const canNotSwipeToNavigate = useMemo(
    () => Boolean(selectedContent || isScrollingInTable || isWritingSomething),
    [isWritingSomething, selectedContent, isScrollingInTable],
  );

  // Keep track of the main item height so that we can restrict the height of the off-screen previews.
  // Otherwise we end up with unexplained whitespace at the bottom of the page.
  const mainItemSlideRef = useRef();
  const mainItemHeight = mainItemSlideRef.current?.clientHeight;

  const cleanupBookIndexCache = () => {
    apolloClient.cache.evict({
      fieldName: 'BookIndex',
      args: { bookId },
      broadcast: false,
    });
  };

  // Lazy because we only want it to run when the bookId changes - its triggered in the useEffect below
  const [loadBookIndex, { loading, data, fetchMore, refetch }] = useLazyQuery(
    SwipeableContentPreviewQuery,
    {
      ssr: false,
      onError: err => {
        console.warn('Error fetching book index', err);
        loadingMore.current = false;
      },
    },
  );
  // Defer rendering until well after page load
  useEffect(
    () => {
      if (!ready) {
        if (canUseIdleCallback) {
          readyTimerRef.current = requestIdleCallback(() => setReady(true));
        } else {
          readyTimerRef.current = setTimeout(() => setReady(true), 2000);
        }
        return () => {
          if (readyTimerRef.current) {
            if (canUseIdleCallback) {
              cancelIdleCallback(readyTimerRef.current);
            } else {
              clearTimeout(readyTimerRef.current);
            }
            readyTimerRef.current = null;
          }
        };
      }
    },
    [ready],
  );
  // Only run the query again if the bookId changes
  useEffect(
    () => {
      if (ready) {
        if (items[0] !== null) {
          setCursor(DEFAULT_CURSOR);
          setMainItemIndex(MIDDLE_PAGE);
          setItems(
            new Array(BOOK_INDEX_SIZE).map((_, index) => (index === MIDDLE_PAGE ? { id } : null)),
          );
        }
        loadBookIndex({
          variables: {
            contentId: id,
            bookId,
            limit: BOOK_INDEX_SIZE,
            offset: 0,
          },
        });
        return () => {
          cleanupBookIndexCache();
        };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ready, bookId, loadBookIndex],
  );

  useEffect(
    () => {
      // When the ID changes the cursor should move to the main item.
      if (data && items?.length && typeof refetch === 'function') {
        const pos = items.findIndex(item => item?.id === id);
        if (pos === -1) {
          // The item we are trying to view is not in the book index. Fetch again.
          cleanupBookIndexCache();
          refetch({
            contentId: id,
            bookId,
            limit: BOOK_INDEX_SIZE,
            offset: 0,
          });
        } else {
          setMainItemIndex(pos);
          if (pos !== cursor.index) {
            setCursor({ index: pos, animate: !disableAnimation });
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id],
  );

  useEffect(
    () => {
      const BookIndex = data?.BookIndex;
      if (BookIndex) {
        const currentCursorItem = cursor.index >= 0 ? items[cursor.index] : null;
        let newMainItemIndex = -1;
        let newCursorPosition = -1;
        setItems(
          BookIndex.map((item, idx) => {
            if (newMainItemIndex < 0 && item.id === id) {
              newMainItemIndex = idx;
            }
            if (currentCursorItem && newCursorPosition < 0 && item.id === currentCursorItem.id) {
              newCursorPosition = idx;
            }
            return { id: item.id, title: item.title };
          }),
        );
        setMainItemIndex(newMainItemIndex);
        const newCursorIndex = newCursorPosition === -1 ? newMainItemIndex : newCursorPosition;
        if (newCursorIndex !== cursor.index) {
          setCursor({
            index: newCursorIndex,
            animate: false,
          });
        }
      }
      loadingMore.current = false;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.BookIndex],
  );

  useEffect(
    () => () => {
      [
        'BackLinks',
        'BookIndex',
        'ContentItem',
        'MeteredAccess',
        'Recipe',
        'RecipeCommentCounts',
        'RelatedRecipes',
        'RelatedReferences',
        'UserComments',
        'UserRecipeFeedback',
      ].forEach(fieldName => {
        apolloClient.cache.evict({ fieldName, broadcast: false });
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const completeNavigation = () => {
    if (pendingNavigation.current) {
      history.push(route.content(pendingNavigation.current.id, pendingNavigation.current.title), {
        animate: true,
      });
      settledTimeout.current = null;
      pendingNavigation.current = null;
      lastViewChangeTargetIndex.current = 0;
    }
  };

  const cancelNavigation = () => {
    if (settledTimeout.current) {
      clearTimeout(settledTimeout.current);
      settledTimeout.current = null;
      pendingNavigation.current = null;
    }
  };

  // Wait a small amount of time to see if the user starts to swipe again
  const beginNavigation = useCallback((target, params) => {
    if (target) {
      pendingNavigation.current = target;
      if (settledTimeout.current) {
        clearTimeout(settledTimeout.current);
      }
      settledTimeout.current = setTimeout(completeNavigation, params?.timeout || 700);
    }
  }, []);

  const updateScrollPosition = touchScrollEvent => {
    const isEventInsideTable =
      touchScrollEvent && touchScrollEvent.target.closest('.table-wrapper');
    setIsScrollingInTable(isEventInsideTable);

    if (isEventInsideTable || canNotSwipeToNavigate) {
      return;
    }

    if (containerRef.current) {
      const transform = `translateY(${window.scrollY}px)`;
      Array.from(
        containerRef.current.getElementsByClassName('swipeable-content-view__preview'),
      ).forEach(slideEl => {
        slideEl.style.transform = transform;
      });
    }
  };

  // Keyboard navigation
  const handleKey = useCallback(
    event => {
      if (canNotSwipeToNavigate) {
        return;
      }
      // Respond to arrow keys for navigation only if we are not focussed on something
      if (
        event.target.nodeName === 'BODY' ||
        event.target.tabIndex === -1 ||
        event.target.classList.contains('allow-arrow-keys')
      ) {
        switch (event.code) {
          case 'ArrowLeft':
            if (cursor.index > 0) {
              updateScrollPosition();
              setCursor({ index: cursor.index - 1, animate: true });
              beginNavigation(items[cursor.index - 1], { timeout: 500 });
            }
            break;
          case 'ArrowRight':
            if (cursor.index < items.length - 1) {
              updateScrollPosition();
              setCursor({ index: cursor.index + 1, animate: true });
              beginNavigation(items[cursor.index + 1], { timeout: 500 });
            }
            break;
          default:
            return;
        }
        if (!keyboardFeatureUsageNotified) {
          dispatch(featureUsed('feature_keyboard_navigation'));
          keyboardFeatureUsageNotified = true;
        }
      }
    },
    [cursor, setCursor, beginNavigation, dispatch, items],
  );
  useEffect(
    () => {
      document.addEventListener('keydown', handleKey, true);
      return () => {
        document.removeEventListener('keydown', handleKey, true);
      };
    },
    [handleKey],
  );

  // Do we need to load more items?
  useEffect(
    () => {
      if (!loading && !loadingMore.current && fetchMore && items.length && cursor.index >= 0) {
        if (cursor.index > items.length * 0.8) {
          fetchMore({ variables: { offset: items.length } });
          loadingMore.current = true;
        } else if (cursor.index < items.length * 0.2) {
          fetchMore({
            variables: { offset: items.length * -1 },
          });
          loadingMore.current = true;
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading, cursor.index, items],
  );
  // Make sure that nothing on the preview content can be focussed.
  useEffect(() => {
    Array.from(
      containerRef.current.getElementsByClassName('swipeable-content-view__preview'),
    ).forEach(slideEl => {
      Array.from(
        slideEl.querySelectorAll('a[href], button, input, [tabindex]:not([tabindex="-1"])'),
      ).forEach(focussableElement => {
        focussableElement.setAttribute('tabindex', '-1');
      });
    });
  });

  // User lifted their finger and has dragged far enough to cause the page to change
  const handleViewChange = newIndex => {
    if (canNotSwipeToNavigate) {
      return;
    }

    if (newIndex !== cursor.index) {
      setCursor({ index: newIndex, animate: true });
    }
    beginNavigation(items[newIndex]);
    if (!swipeFeatureUsageNotified) {
      dispatch(featureUsed('feature_swipe_navigation'));
      swipeFeatureUsageNotified = true;
    }
    // Saved so we can ignore late arriving handleSwitching events
    lastViewChangeTargetIndex.current = newIndex;
  };

  // The user is dragging their finger across the screen
  const handleSwitching = fromIndex => {
    // On Android we sometimes see late arriving handleSwitching events after the handleViewChange event.
    // These pertain to the previous gesture and should be dropped. Thats why we check the fromIndex.
    if (!lastViewChangeTargetIndex.current || fromIndex === lastViewChangeTargetIndex.current) {
      cancelNavigation();
    }
  };

  const mainItem = <ContentView id={id} {...otherProps} />;

  // eslint-disable-next-line react/prop-types
  const slideRenderer = ({ index, key }) => {
    if (index === mainItemIndex) {
      return (
        <div
          ref={mainItemSlideRef}
          key={key}
          className={classNames(slideClassName, 'swipeable-content-view__main')}
        >
          {mainItem}
        </div>
      );
    }
    const item = items[index];
    // The preview slide may be taller than the visible content.
    // Restrict its height and hide any overflow to avoid whitespace at the end of the recipe.
    const previewSlideStyle = mainItemHeight
      ? { maxHeight: mainItemHeight, overflowY: 'hidden' }
      : null;
    if (item) {
      return (
        <div
          key={key}
          className={classNames(slideClassName, 'do-not-print')}
          style={previewSlideStyle}
        >
          <div className="swipeable-content-view__preview">
            <ContentView previewOnly id={item.id} type={route.contentType(item.id)} />
          </div>
        </div>
      );
    }
    return (
      <div
        key={key}
        className={classNames(slideClassName, 'do-not-print')}
        style={previewSlideStyle}
      >
        <div className="swipeable-content-view__preview" />
      </div>
    );
  };
  return (
    <div
      className="layout--normal"
      ref={containerRef}
      onTouchStart={updateScrollPosition}
      onTouchEnd={updateScrollPosition}
    >
      <VirtualizeSwipeableViews
        animateTransitions={cursor.animate}
        disabled={!ready || items[0] === null || canNotSwipeToNavigate}
        ignoreNativeScroll
        index={cursor.index}
        onChangeIndex={handleViewChange}
        onSwitching={() => handleSwitching(cursor.index)}
        overscanSlideAfter={1}
        overscanSlideBefore={1}
        slideCount={items.length}
        slideRenderer={slideRenderer}
        hysteresis={0.75}
      />
    </div>
  );
}

SwipeableContentView.propTypes = {
  id: PropTypes.string.isRequired,
  className: PropTypes.string,
  slideClassName: PropTypes.string,
  disableAnimation: PropTypes.bool,
};

SwipeableContentView.defaultProps = {
  className: null,
  slideClassName: null,
  disableAnimation: false,
};

export default SwipeableContentView;
